/**
 * History API (F4). Mirrors PRD §7 REST surface:
 *   GET /sessions (?q=&lang=&from=&to=)      — list/search
 *   GET /sessions/{id}                        — transcript + artifacts
 *   PATCH /sessions/{id}                      — rename
 *   DELETE /sessions/{id}                     — delete
 *   GET /sessions/{id}/export?format=…        — download (browser navigates)
 *
 * Shapes match the `transcripts` / `artifacts` tables (PRD §6): full_text,
 * segments_json = [{segment_id, ts_ms, src, text, translated}], and artifact
 * kinds (audio_compiled|export_srt|…). We reuse the Session row type from the
 * F1 session client so list rows stay a single source of truth.
 */

import { http, API_BASE } from '@/shared/api/http';
import type { Session } from '@/features/session/sessionApi';
export type { Session } from '@/features/session/sessionApi';

/** One finalized transcript segment (transcripts.segments_json entry). */
export interface TranscriptSegment {
  segment_id: string;
  /** Offset from session start, in milliseconds. */
  ts_ms: number;
  /** Source (original) text. */
  src: string;
  /** Translated text in the session target language. */
  translated: string;
}

/** A stored artifact for a session (artifacts table row). */
export interface SessionArtifact {
  /** audio_compiled | export_srt | export_vtt | export_txt | export_json | … */
  kind: string;
  mime: string;
  /** Signed/download URL (may be absolute or API-relative). */
  url: string;
}

/** Response of GET /sessions/{id} — the session plus its transcript & artifacts. */
export interface SessionDetail extends Session {
  full_text: string;
  segments: TranscriptSegment[];
  artifacts: SessionArtifact[];
}

/** Optional filters for GET /sessions. */
export interface HistoryQuery {
  q?: string;
  lang?: string;
  /** ISO date (yyyy-MM-dd) lower bound. */
  from?: string;
  /** ISO date (yyyy-MM-dd) upper bound. */
  to?: string;
}

/** Export formats supported by GET /sessions/{id}/export?format=… (PRD §7). */
export type ExportFormat = 'txt' | 'srt' | 'vtt' | 'json' | 'mp3' | 'wav';

function buildQuery(query: HistoryQuery): string {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.lang) params.set('lang', query.lang);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const historyApi = {
  /** List/search sessions, newest-first ordering is the backend's default. */
  list(query: HistoryQuery = {}): Promise<Session[]> {
    return http.get<Session[]>(`/sessions${buildQuery(query)}`);
  },

  /** Full session detail: transcript full_text + segments + artifacts. */
  detail(id: string): Promise<SessionDetail> {
    return http.get<SessionDetail>(`/sessions/${encodeURIComponent(id)}`);
  },

  /** Rename a session (PATCH /sessions/{id}). */
  rename(id: string, name: string): Promise<Session> {
    return http.patch<Session>(`/sessions/${encodeURIComponent(id)}`, {
      json: { name },
    });
  },

  /** Permanently delete a session (DELETE /sessions/{id}). */
  remove(id: string): Promise<void> {
    return http.delete<void>(`/sessions/${encodeURIComponent(id)}`);
  },

  /**
   * Build the export download URL. The browser navigates to it directly so the
   * response's Content-Disposition drives the download (auth is via the
   * Bearer flow the backend also accepts on this route, or a signed URL).
   */
  exportUrl(id: string, format: ExportFormat): string {
    return `${API_BASE}/sessions/${encodeURIComponent(id)}/export?format=${format}`;
  },
};
